import React, { useState } from "react";
import { X, Mail, KeyRound, Lock, CheckCircle2 } from "lucide-react";
import { api } from "../api/client";

export default function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState("email"); // email | otp | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setStep("otp");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al enviar código");
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) return setError("Las contraseñas no coinciden");
    if (password.length < 8) return setError("Mínimo 8 caracteres");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: password,
      });
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Código inválido");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="forgot-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded hover:bg-neutral-100" data-testid="forgot-close">
          <X className="w-5 h-5 text-neutral-500" />
        </button>

        {step === "email" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#EEF0FC] flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#3949AB]" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-neutral-900">Recuperar contraseña</h2>
                <p className="text-xs text-neutral-500">Te enviaremos un código por WhatsApp</p>
              </div>
            </div>
            <form onSubmit={requestOTP} className="space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Correo registrado</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="tu@correo.pe"
                  required
                  className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none"
                  data-testid="forgot-email"
                />
              </div>
              {error && <div className="chip chip-danger text-sm">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary !py-3 justify-center"
                data-testid="forgot-send"
              >
                {loading ? "Enviando..." : "Enviar código"}
              </button>
            </form>
          </>
        )}

        {step === "otp" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#EEF0FC] flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-[#3949AB]" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-neutral-900">Verifica el código</h2>
                <p className="text-xs text-neutral-500">Revisa tu WhatsApp — código válido 10 min</p>
              </div>
            </div>
            <form onSubmit={submitReset} className="space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Código de 6 dígitos</label>
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  maxLength={6}
                  required
                  className="w-full mt-1.5 px-3 py-3 border border-neutral-300 rounded text-center text-2xl tracking-widest font-mono focus:border-[#3949AB] outline-none"
                  data-testid="forgot-otp"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Nueva contraseña</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  minLength={8}
                  required
                  className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none"
                  data-testid="forgot-newpass"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Confirmar contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  className="w-full mt-1.5 px-3 py-2.5 border border-neutral-300 rounded text-sm focus:border-[#3949AB] outline-none"
                  data-testid="forgot-confirmpass"
                />
              </div>
              {error && <div className="chip chip-danger text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="w-full btn-primary !py-3 justify-center" data-testid="forgot-reset">
                {loading ? "Cambiando..." : "Cambiar contraseña"}
              </button>
              <button type="button" onClick={() => setStep("email")} className="w-full text-xs text-neutral-500 hover:text-[#3949AB]">
                ← Volver
              </button>
            </form>
          </>
        )}

        {step === "done" && (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-display text-xl font-bold text-neutral-900 mb-2">¡Contraseña actualizada!</h2>
            <p className="text-sm text-neutral-500 mb-6">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <button onClick={onClose} className="btn-primary !py-2.5 !px-6" data-testid="forgot-done">
              Ir al inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
